import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Link,
  Radio,
  RadioGroup,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { handleHookFormHTTPError } from "src/common/form";
import { HTTPError, useKy } from "src/common/ky";
import { YupPossiblePhoneNumber } from "src/common/phone-numbers";
import { PhoneNumberInput } from "src/common/PhoneNumbers";
import { noop } from "src/common/util";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";

interface InvitationValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
}

const InvitationValidationSchema = Yup.object().shape({
  firstName: Yup.string().required().label("First Name"),
  lastName: Yup.string().required().label("Last Name"),
  email: Yup.string().label("Email"),
  phone: YupPossiblePhoneNumber({ required: true }),
  role: Yup.string().required().label("Role"),
});

export const StaffInvite = () => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm<InvitationValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "staff",
    },
    resolver: yupResolver(InvitationValidationSchema),
  });
  const ky = useKy();
  const toast = useToast();

  const navigate = useNavigate();
  const inviteUser = useMutation<void, HTTPError, InvitationValues>(
    async (values: InvitationValues) => {
      await ky.post("staff-users", {
        json: { ...values },
      });
    },
    {
      onSuccess: () => {
        navigate("/staff");
      },
      onError: handleHookFormHTTPError(setError, getValues, toast),
    }
  );

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={"/staff"}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Add Staff Member</PageHeaderTitle>
        <PageHeaderSubtitle>
          Enter the new staff member details here
        </PageHeaderSubtitle>
      </PageHeader>
      <PageContent>
        <form
          onSubmit={handleSubmit((data) =>
            inviteUser.mutateAsync(data).catch(noop)
          )}
        >
          <Grid gap={4}>
            <GridItem colSpan={1}>
              <Controller
                name="firstName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isInvalid={fieldState.invalid && fieldState.isTouched}
                  >
                    <FormLabel htmlFor="firstName">First Name</FormLabel>
                    <Input {...field} id="firstName" />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem colSpan={1}>
              <Controller
                name="lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isInvalid={fieldState.invalid && fieldState.isTouched}
                  >
                    <FormLabel htmlFor="lastName">Last Name</FormLabel>
                    <Input {...field} id="lastName" />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem colSpan={1}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isInvalid={fieldState.invalid && fieldState.isTouched}
                  >
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <Input {...field} id="email" />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem colSpan={1}>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isInvalid={fieldState.invalid && fieldState.isTouched}
                  >
                    <FormLabel htmlFor="phone">Phone Number</FormLabel>
                    <PhoneNumberInput {...field} control={control} id="phone" />
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            <GridItem>
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    isRequired
                    isInvalid={fieldState.invalid && fieldState.isTouched}
                  >
                    <FormLabel htmlFor="role">Role</FormLabel>
                    <RadioGroup id="role" {...field}>
                      <Stack direction="column">
                        <Radio value="admin">Admin</Radio>
                        <Radio value="staff">Staff</Radio>
                      </Stack>
                    </RadioGroup>
                    <FormErrorMessage>
                      {fieldState.error?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />
            </GridItem>

            {inviteUser.error && (
              <GridItem colSpan={2}>
                <Alert status="error">
                  <AlertIcon />
                  There was a problem adding the staff member.
                </Alert>
              </GridItem>
            )}

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem colSpan={2}>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to={`/staff`}>
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Save Staff Member
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </form>
      </PageContent>
    </>
  );
};
